import { useChildAccounts } from './useChildAccounts';
import { formatAuthError } from '../utils/formatAuthError';

export function useAuth() {
	const { $api, $apiAuth } = useNuxtApp();

	const { isStaticMode, staticConfig } = useStaticMode();

	const authState = useState('lcp-auth', () => ({
		isLoggedIn: false,
		isLoggingIn: false,
		userID: null as string | null,
	}));

	// Once a refresh attempt fails, block all further API calls until the
	// user successfully logs in.  Cleared on login; reset on full page reload.
	const authCheckFailed = useState<boolean>('lcp-auth-check-failed', () => false);

	const loginError = ref<string | null>(null);

	// Child accounts functionality
	const childAccounts = useChildAccounts({
		stateKey: 'lcp-child-accounts',
		onLogout: () => logout(),
	});

	function clearLoginErrorMessage() {
		loginError.value = null;
	}

	async function authenticateWithToken(accessToken: string, userName?: string) {
		authCheckFailed.value = false;
		$apiAuth.authenticate(accessToken);
		authState.value.isLoggedIn = true;
		if (userName) {
			authState.value.userID = userName;
		}

		frogger.info('login succeeded', {
			userName,
			mode: isStaticMode ? 'static' : 'ssr',
			method: '2fa',
		});
	}

	async function logIn(userName: string, password: string) {
		loginError.value = null;

		if (!userName || !password) {
			loginError.value = 'Please enter a valid username and password.';
			return false;
		}

		try {
			authState.value.isLoggingIn = true;

			// Runs inside the caller's span('login') (login.vue), so this parents correctly.
			const traceHeaders = useTraceHeaders();

			let response: { accessToken: string };

			if (isStaticMode) {
				// Static mode: call backend directly (no Nuxt server proxy)
				// RefreshToken cookie will be set on the backend's domain
				frogger.debug('login request (static mode, direct backend)', {
					apiOrigin: staticConfig?.apiOrigin,
				});
				response = await $fetch<{ accessToken: string }>(
					`${staticConfig?.apiOrigin}/api/Auth/login`,
					{
						method: 'POST',
						body: { User: userName, Pwd: password },
						credentials: 'include', // Required for CORS cookies
						headers: traceHeaders,
					},
				);
			} else {
				// SSR mode: use Nuxt proxy which handles RefreshToken storage
				frogger.debug('login request (ssr proxy)');
				response = await $fetch<{ accessToken: string }>('/api/Auth/login', {
					method: 'POST',
					body: { User: userName, Pwd: password },
					credentials: 'include',
					headers: traceHeaders,
				});
			}

			frogger.debug('login response received', {
				hasAccessToken: !!response?.accessToken,
			});

			if (response?.accessToken) {
				authCheckFailed.value = false;
				$apiAuth.authenticate(response.accessToken);
				authState.value.isLoggedIn = true;
				authState.value.userID = userName;

				frogger.info('login succeeded', {
					userName,
					mode: isStaticMode ? 'static' : 'ssr',
					method: 'password',
					hasAccessToken: true,
				});

				// Child accounts are fetched by the app.vue watcher when
				// isLoggedIn transitions to true — no need to call here.

				return true;
			}

			throw new Error('Token not found in response');
		} catch (error: unknown) {
			authState.value.isLoggedIn = false;

			const statusCode =
				error && typeof error === 'object' && 'statusCode' in error
					? (error as { statusCode: number }).statusCode
					: undefined;

			const loginFailureContext = {
				statusCode,
				userName,
				mode: isStaticMode ? 'static' : 'ssr',
			};
			if (statusCode !== undefined && statusCode >= 500) {
				frogger.error('login failed', loginFailureContext);
			} else {
				frogger.warn('login failed', loginFailureContext);
			}

			loginError.value = formatAuthError(error, {
				badRequest: 'Incorrect username or password, please try again.',
				generic: 'An error occurred during login. Please try again later.',
			});

			return false;
		} finally {
			authState.value.isLoggingIn = false;
		}
	}

	async function logout(redirect: string = '/login?logout=true') {
		// Signal that an intentional logout is in progress so the API
		// interceptor's forceLogout() won't navigate away prematurely
		// (e.g. when concurrent requests receive 401 after the session
		// is invalidated server-side).
		const intentionalLogout = useState<boolean>('lcp-intentional-logout');
		intentionalLogout.value = true;

		const mode = isStaticMode ? 'static' : 'ssr';

		await frogger.span('logout', async () => {
			try {
				if (isStaticMode) {
					// Static mode: call backend directly
					frogger.info('logout initiated', { mode: 'static' });
					await $fetch(`${staticConfig!.apiOrigin}/api/Auth/logout`, {
						method: 'GET',
						credentials: 'include',
						headers: useTraceHeaders(),
					});
				} else {
					// SSR mode: use proxy
					frogger.info('logout initiated', { mode: 'ssr' });
					await $fetch('/api/Auth/logout', {
						method: 'GET',
						credentials: 'include',
						headers: useTraceHeaders(),
					});
				}
			} catch (error) {
				frogger.warn('logout request failed; clearing local session anyway', error);
			}

			$apiAuth.deauthenticate();
			authState.value.isLoggedIn = false;
			authState.value.userID = null;
			childAccounts.reset();

			// Tell the service worker to purge all cached content so the next
			// page load fetches fresh assets (prevents stale versions after deploy).
			if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
				navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_AUTH_CACHE' });
			}

			frogger.info('logout completed', { mode });

			await navigateTo(redirect, { external: true });
		});
	}

	async function verifyAuthentication(): Promise<boolean> {
		if (authState.value.isLoggedIn) {
			return true;
		}

		// A previous refresh already failed; don't retry until the user
		// successfully logs in (which clears the flag).
		if (authCheckFailed.value) {
			return false;
		}

		return frogger.span('auth-verify', async () => {
			const { portalType } = useCustomerConfig();
			const noRefreshToken = portalType.value === 'supplier';

			let newToken: string | null = null;

			// Check if token already exists (e.g., set by SSR plugin)
			const existingToken = $apiAuth.getToken();
			if (existingToken) {
				newToken = existingToken;
			} else if (noRefreshToken) {
				// Mapping/supplier portals have no refresh token; skip the
				// refresh attempt which would always fail and trigger a logout.
				newToken = null;
			} else if (isStaticMode) {
				// Static mode: call backend directly for refresh
				try {
					const response = await $fetch<{ accessToken: string }>(
						`${staticConfig!.apiOrigin}/api/Auth/refresh`,
						{
							method: 'POST',
							credentials: 'include',
							headers: useTraceHeaders(),
						},
					);
					newToken = response.accessToken;
				} catch (error) {
					// This is the production (static) session-death path; must be visible.
					frogger.warn('token refresh failed (static mode)', error, {
						apiOrigin: staticConfig?.apiOrigin,
					});
					newToken = null;
				}
			} else {
				// SSR mode: use the attemptTokenRefresh from $apiAuth (goes through proxy)
				newToken = await $apiAuth.attemptTokenRefresh();
			}

			if (newToken) {
				$apiAuth.authenticate(newToken);
			} else {
				// No token available; mark auth as failed so subsequent
				// navigations skip verification entirely until a real login.
				frogger.info('session ended, no auth token available', {
					mode: isStaticMode ? 'static' : 'ssr',
					noRefreshToken,
				});
				authCheckFailed.value = true;
				authState.value.isLoggedIn = false;
				authState.value.userID = null;
				return false;
			}

			try {
				const response = await $api<Record<string, unknown>>('/api/Auth/isAuthenticated');

				// Defensive: handle both camelCase and PascalCase from .NET backend
				const isAuthenticated = response.isAuthenticated ?? response.IsAuthenticated;
				const userId = (response.userId ?? response.UserId) as string | undefined;

				if (isAuthenticated) {
					authState.value.isLoggedIn = true;
					authState.value.userID = userId ?? null;
				} else {
					frogger.warn('session rejected by backend', {
						hadToken: true,
						isAuthenticated: false,
					});
				}
			} catch (error: unknown) {
				const err = error as { status?: number; statusCode?: number };
				frogger.warn('authentication check failed', error, {
					status: err.status || err.statusCode,
				});
			}

			if (authState.value.isLoggedIn) {
				// Child accounts are fetched by the app.vue watcher when
				// isLoggedIn transitions to true; no need to call here.
				return true;
			}

			authCheckFailed.value = true;
			authState.value.isLoggedIn = false;
			authState.value.userID = null;
			return false;
		});
	}

	return {
		state: authState,
		logIn,
		authenticateWithToken,
		logOut: logout,
		loginErrorMessage: computed(() => loginError.value),
		clearLoginErrorMessage,
		loggedIn: computed(() => authState.value.isLoggedIn),
		loggingIn: computed(() => authState.value.isLoggingIn),
		currentUserID: computed(() => authState.value.userID),
		verifyAuthentication,
		isStaticMode,

		// Child accounts
		childAccounts: childAccounts.childAccounts,
		childAccountsOptions: childAccounts.childAccountsOptions,
		selectedAccount: childAccounts.selectedAccount,
		isLoadingAccounts: childAccounts.isLoadingAccounts,
		isChangingUser: childAccounts.isChangingUser,
		accountsError: childAccounts.accountsError,
		getSelectedAccountOption: childAccounts.getSelectedAccountOption,
		fetchChildAccounts: childAccounts.fetchChildAccounts,
		changeUser: childAccounts.changeUser,
	};
}
